// ScheduleLoader.js — "build your whole day in 10 seconds." A MODAL (centered
// over the tray, not an inline panel you have to scroll to). Three inputs all
// hitting /v1/synapse/schedule/parse: type it, say it (short mic clip), or
// drop an EMR screenshot. Parse returns PROPOSED visits which you can EDIT
// (name / time / minutes) before creating — so a slightly-off parse is a quick
// fix, not a re-do. Confirm actually creates them. Design: docs/comprehend-live-v05.md.
import React, { useEffect, useRef, useState } from 'react';
import { useComprehendLive } from 'agent/useComprehendLive';
import { T, buttonPrimary, buttonGhost } from './liveTheme';

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const formatDisplayTime = (time) => {
  const raw = String(time || '').trim();
  const match = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a|p)?$/i);
  if (!match) return raw;

  let hour = Number(match[1]);
  const minute = match[2] === undefined ? 0 : Number(match[2]);
  const meridiem = match[3]?.toLowerCase();
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || minute > 59) {
    return raw;
  }

  if (meridiem) {
    if (hour < 1 || hour > 12) return raw;
    return `${hour}:${String(minute).padStart(2, '0')} ${
      meridiem.startsWith('p') ? 'pm' : 'am'
    }`;
  }
  if (hour > 23) return raw;

  const suffix = hour >= 12 ? 'pm' : 'am';
  hour %= 12;
  if (hour === 0) hour = 12;
  return `${hour}:${String(minute).padStart(2, '0')} ${suffix}`;
};

const fieldStyle = {
  background: T.surfaceDeep,
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  color: T.textPrimary,
  fontSize: 12,
  padding: '5px 7px',
  outline: 'none',
};

const ScheduleLoader = ({ onClose }) => {
  const { actions } = useComprehendLive();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [error, setError] = useState(null);
  const [syncCtx, setSyncCtx] = useState(null); // {available, emr, currentPageEMR}
  const recRef = useRef(null);
  const fileRef = useRef(null);

  // Esc closes the modal.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Is calendar sync available for this user's EMR? (CORA / WebPT-in-CX.)
  useEffect(() => {
    let alive = true;
    actions
      .syncCalContext()
      .then((ctx) => alive && setSyncCtx(ctx))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [actions]);

  const syncCalendar = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await actions.syncScheduleCal(syncCtx);
      const missing = result?.['Patients without Schedule'];
      if (missing && missing.length) {
        setError(`Synced — couldn't place: ${missing.join(', ')}.`);
      } else {
        onClose?.();
      }
    } catch (e) {
      setError(e.message || 'Calendar sync failed — try again.');
    } finally {
      setBusy(false);
    }
  };

  const runParse = async (payload) => {
    setBusy(true);
    setError(null);
    try {
      const rsp = await actions.parseSchedule(payload);
      if (!rsp || rsp.error) throw new Error(rsp?.error || 'parse failed');
      setProposals(
        (rsp.visits || []).map((visit) => ({
          ...visit,
          time: visit.time ? formatDisplayTime(visit.time) : visit.time,
        })),
      );
    } catch (e) {
      setError('Could not read that — try again.');
    } finally {
      setBusy(false);
    }
  };

  const recordVoice = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const parts = [];
      const rec = new MediaRecorder(stream);
      recRef.current = rec;
      rec.ondataavailable = (e) => e.data.size > 0 && parts.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(parts, { type: rec.mimeType || 'audio/webm' });
        if (blob.size > 0) runParse({ audio: await blobToDataURL(blob) });
      };
      rec.start();
      setRecording(true);
    } catch (e) {
      setError('Microphone unavailable.');
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    runParse({ image: await blobToDataURL(file) });
    e.target.value = '';
  };

  const editProposal = (i, key, value) =>
    setProposals((ps) =>
      ps.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)),
    );
  const removeProposal = (i) =>
    setProposals((ps) => ps.filter((_, idx) => idx !== i));

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await actions.createVisitsFromProposals(proposals);
      const unresolved = res?.unresolved || [];
      if (unresolved.length) {
        // Each named visit becomes an unassigned conversation (no patient) —
        // anything that fails to create stays open so it can be retried; the
        // ones that succeeded drop off the list.
        const stuck = new Set(unresolved.map((u) => u.name));
        setProposals((ps) =>
          ps.filter((p) => stuck.has((p.patientName || '').trim())),
        );
        setError(
          `Couldn't create — ${unresolved
            .map((u) => u.name)
            .join(', ')}. Try again.`,
        );
        return;
      }
      setProposals(null);
      setText('');
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  const label = { fontSize: 11, color: T.textFaint, fontWeight: 700 };

  return (
    // Modal: full-cover backdrop + centered card, above the tray.
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000004,
        background: 'rgba(7,20,16,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="cpl-scroll"
        style={{
          width: '100%',
          maxWidth: 460,
          maxHeight: '85vh',
          overflowY: 'auto',
          background: T.surfaceCard,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          padding: 16,
          boxShadow: '0 18px 50px rgba(0,0,0,0.5)',
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}
        >
          <span style={label}>LOAD YOUR DAY</span>
          <i
            className="fa fa-times"
            aria-hidden="true"
            onClick={onClose}
            style={{
              marginLeft: 'auto',
              color: T.textSecondary,
              fontSize: 14,
              cursor: 'pointer',
              padding: 2,
            }}
          />
        </div>

        {!proposals && (
          <>
            {/* Calendar sync — the one-tap path when the EMR supports it. */}
            {syncCtx?.available && (
              <>
                <button
                  style={{
                    ...buttonPrimary,
                    width: '100%',
                    opacity: busy ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 7,
                  }}
                  disabled={busy}
                  onClick={syncCalendar}
                >
                  <i className="fa fa-calendar-check" aria-hidden="true" />
                  {busy ? 'Syncing…' : 'Sync from your calendar'}
                </button>
                <p
                  style={{
                    margin: '8px 0 10px',
                    textAlign: 'center',
                    fontSize: 10,
                    color: T.textFaint,
                  }}
                >
                  or build it by hand
                </p>
              </>
            )}
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Rattle off your day — “Annie at 9, Jimmy 9:30, Marcus and Elena at 10…”"
              rows={3}
              style={{ ...fieldStyle, width: '100%', resize: 'none' }}
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <button
                style={{ ...buttonPrimary, flex: 1, opacity: busy ? 0.6 : 1 }}
                disabled={busy || !text.trim()}
                onClick={() => runParse({ text })}
              >
                {busy ? 'Reading…' : 'Load'}
              </button>
              <button
                style={{
                  ...buttonGhost,
                  background: recording ? 'rgba(245,54,92,0.18)' : undefined,
                  color: recording ? T.redSoft : undefined,
                }}
                disabled={busy}
                onClick={recordVoice}
                title="Say your day"
              >
                <i className="fa fa-microphone" aria-hidden="true" />{' '}
                {recording ? 'Stop' : 'Say it'}
              </button>
              <button
                style={buttonGhost}
                disabled={busy}
                onClick={() => fileRef.current?.click()}
                title="Upload an EMR screenshot"
              >
                <i className="fa fa-image" aria-hidden="true" />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onFile}
                style={{ display: 'none' }}
              />
            </div>
            {error && (
              <p style={{ margin: '8px 0 0', fontSize: 11, color: T.redSoft }}>
                {error}
              </p>
            )}
          </>
        )}

        {proposals && (
          <>
            <p style={{ ...label, margin: '0 0 8px' }}>
              REVIEW &amp; EDIT — {proposals.length} VISIT
              {proposals.length === 1 ? '' : 'S'}
            </p>
            {proposals.length === 0 && (
              <p style={{ fontSize: 11, color: T.textSecondary }}>
                Nothing found. Go back and try again.
              </p>
            )}
            {error && (
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: 11,
                  color: T.redSoft,
                  lineHeight: 1.4,
                }}
              >
                {error}
              </p>
            )}
            {proposals.map((v, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 7,
                }}
              >
                <input
                  value={v.patientName || ''}
                  onChange={(e) =>
                    editProposal(i, 'patientName', e.target.value)
                  }
                  placeholder="Visit name"
                  style={{ ...fieldStyle, flex: 1, minWidth: 0 }}
                />
                <input
                  value={v.time || ''}
                  onChange={(e) => editProposal(i, 'time', e.target.value)}
                  onBlur={(e) =>
                    editProposal(i, 'time', formatDisplayTime(e.target.value))
                  }
                  placeholder="—"
                  title="Start time (e.g. 10:30 am)"
                  style={{ ...fieldStyle, width: 84, textAlign: 'center' }}
                />
                <input
                  type="number"
                  value={v.durationMin || 30}
                  onChange={(e) =>
                    editProposal(i, 'durationMin', Number(e.target.value) || 30)
                  }
                  title="Minutes"
                  style={{ ...fieldStyle, width: 50, textAlign: 'center' }}
                />
                <i
                  className="fa fa-times"
                  aria-hidden="true"
                  onClick={() => removeProposal(i)}
                  title="Remove"
                  style={{
                    color: T.textFaint,
                    fontSize: 12,
                    cursor: 'pointer',
                    padding: 2,
                  }}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
              <button
                style={{ ...buttonPrimary, flex: 1, opacity: busy ? 0.6 : 1 }}
                disabled={busy || proposals.length === 0}
                onClick={confirm}
              >
                {busy ? 'Creating…' : `Create ${proposals.length}`}
              </button>
              <button
                style={buttonGhost}
                disabled={busy}
                onClick={() => setProposals(null)}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ScheduleLoader;
